import type { EstadoPropuesta, FormatoPieza, Propuesta } from "../tipos";
import { respaldoEsVerificable } from "../tipos";
import { formatNumero } from "./formatoNumero";

/**
 * Tarjeta de una propuesta del Plan.
 *
 * Acá vive la compuerta central del producto: una propuesta solo se dibuja
 * si `respaldoEsVerificable` da true para su respaldo, o si su estado es
 * `en_observacion` (la única excepción — ese estado lleva respaldo vacío a
 * propósito porque todavía no propone nada). Si no se cumple ninguna de las
 * dos, la tarjeta no se dibuja y queda un `console.warn` con el título: un
 * agente que inventa un número no puede sonar tan convincente como uno que
 * lo cita bien.
 */

interface PropuestaCardProps {
  propuesta: Propuesta;
  onVerPieza: (piezaId: string) => void;
}

const ETIQUETAS_ESTADO: Record<EstadoPropuesta, { texto: string; clases: string }> = {
  listo: { texto: "Listo", clases: "bg-[#DDEFE7] text-[#1D7A57]" },
  generando: { texto: "Generando", clases: "bg-[#60E7F0]/[0.25] text-[#01269B]" },
  propuesta: { texto: "Propuesta", clases: "bg-[#1644EB]/[0.1] text-[#1644EB]" },
  en_observacion: { texto: "En observación", clases: "bg-[#02062B]/[0.06] text-[#02062B]/[0.55]" },
};

const ETIQUETAS_FORMATO: Record<FormatoPieza, string> = {
  reel_9x16: "Reel 9:16",
  feed_1x1: "Feed 1:1",
  feed_4x5: "Feed 4:5",
  carrusel_4x5: "Carrusel 4:5",
};

// Los nombres de métrica del ejemplo son un conjunto chico y fijo (Bloque A);
// acá se traducen a algo legible. Lo que no está en el diccionario se
// muestra igual, solo con los guiones bajos cambiados por espacios.
const ETIQUETAS_METRICA: Record<string, string> = {
  retencion_p75: "retención p75",
  dias_sin_contenido: "días sin contenido",
  frecuencia: "frecuencia",
  saves_relativo: "saves relativo",
};

function formatMetrica(metrica: string): string {
  return ETIQUETAS_METRICA[metrica] ?? metrica.replace(/_/g, " ");
}

const claseEtiqueta = "rounded-full py-1 px-2.5 text-[11px] font-medium";

export const PropuestaCard = ({ propuesta, onVerPieza }: PropuestaCardProps) => {
  const esObservacion = propuesta.estado === "en_observacion";

  if (!esObservacion && !respaldoEsVerificable(propuesta.respaldo)) {
    console.warn(
      `PropuestaCard: "${propuesta.titulo}" no se dibuja — su respaldo no es verificable.`,
    );
    return null;
  }

  const estadoInfo = ETIQUETAS_ESTADO[propuesta.estado];

  return (
    <article
      className={`rounded-2xl border p-5 ${
        esObservacion
          ? "border-dashed border-[#02062B]/[0.18] bg-[#FAFAFC]"
          : "border-[#02062B]/[0.08] bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-[15px] font-semibold text-brand-black">{propuesta.titulo}</h3>
        <span className={`shrink-0 rounded-full py-1.5 px-3 text-[12px] font-medium ${estadoInfo.clases}`}>
          {estadoInfo.texto}
        </span>
      </div>

      <p className="mt-2 text-[13px] leading-relaxed text-[#02062B]/[0.7]">{propuesta.por_que}</p>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {propuesta.respaldo.map((item) => (
          <span key={item.fila_origen} className={`${claseEtiqueta} bg-[#1644EB]/[0.08] text-[#01269B]`}>
            {formatMetrica(item.metrica)} {formatNumero(item.valor)}
          </span>
        ))}
        <span className={`${claseEtiqueta} bg-[#02062B]/[0.05] text-[#02062B]/[0.6]`}>
          {ETIQUETAS_FORMATO[propuesta.coordenada.formato]}
        </span>
        {propuesta.creditos_estimados !== null && (
          <span className={`${claseEtiqueta} bg-[#02062B]/[0.05] text-[#02062B]/[0.6]`}>
            {propuesta.creditos_estimados} créditos
          </span>
        )}
      </div>

      {propuesta.duda && (
        <div className="mt-3 border-l-2 border-[#02062B]/[0.15] pl-3 text-[12px] text-[#02062B]/[0.55]">
          {propuesta.duda}
        </div>
      )}

      {!esObservacion && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => onVerPieza(propuesta.pieza_id)}
            className="rounded-full bg-brand-primary py-2 px-4 text-[12px] font-medium text-white"
          >
            Ver pieza
          </button>
        </div>
      )}
    </article>
  );
};

export default PropuestaCard;
